import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Plus, KeyRound, Trash2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useConfirm } from "@/components/ConfirmDialog";

const inp = "px-2.5 py-1.5 rounded border text-sm";
const ROLE_LABEL = { admin: "Admin", operator: "Team member", viewer: "View only" };
const err = (e, d) => {
  const x = e.response?.data?.detail;
  return typeof x === "string" ? x : Array.isArray(x) ? x.map((i) => i.msg).join(" ") : d;
};

function AddAccount({ roles, onDone }) {
  const blank = { email: "", name: "", role: "operator", password: "" };
  const [f, setF] = useState(blank);
  const set = (p) => setF((x) => ({ ...x, ...p }));
  const add = async () => {
    try { await api.post("/admin/users", f); toast.success(`${f.email} added`); setF(blank); onDone(); }
    catch (e) { toast.error(err(e, "Couldn't add account")); }
  };
  return (
    <div className="flex flex-wrap gap-2 items-center pt-2" data-testid="account-add-form">
      <input className={`${inp} flex-1 min-w-[200px]`} placeholder="email@thickandthin.media" value={f.email} onChange={(e) => set({ email: e.target.value })} data-testid="account-add-email" />
      <input className={`${inp} w-40`} placeholder="Name" value={f.name} onChange={(e) => set({ name: e.target.value })} data-testid="account-add-name" />
      <select className={inp} value={f.role} onChange={(e) => set({ role: e.target.value })} data-testid="account-add-role">{roles.map((r) => <option key={r} value={r}>{ROLE_LABEL[r] || r}</option>)}</select>
      <input className={`${inp} w-44`} type="text" placeholder="Starting password (8+)" value={f.password} onChange={(e) => set({ password: e.target.value })} data-testid="account-add-password" />
      <button className="btn btn-primary text-xs" disabled={!f.email || f.password.length < 8} onClick={add} data-testid="account-add-btn"><Plus size={12} />Add</button>
    </div>
  );
}

function AccountRow({ u, roles, me, onDone }) {
  const [pw, setPw] = useState(null);
  const { confirm } = useConfirm();
  const remove = async () => {
    if (!(await confirm({ title: `Remove ${u.name || u.email}?`, body: "They won't be able to sign in anymore. Their leads, tasks and history stay as they are.", danger: true, okLabel: "Remove" }))) return;
    try { await api.delete(`/admin/users/${encodeURIComponent(u.email)}`); toast.success(`${u.email} removed`); onDone(); }
    catch (e) { toast.error(err(e, "Couldn't remove account")); }
  };
  const setRole = async (role) => {
    try { await api.put(`/admin/users/${encodeURIComponent(u.email)}`, { role }); toast.success("Role updated"); onDone(); }
    catch (e) { toast.error(err(e, "Couldn't change role")); }
  };
  const reset = async () => {
    try { await api.post(`/admin/users/${encodeURIComponent(u.email)}/password`, { password: pw }); toast.success(`Password reset for ${u.email}`); setPw(null); }
    catch (e) { toast.error(err(e, "Couldn't reset password")); }
  };
  return (
    <div className="flex flex-wrap items-center gap-2 py-2 border-b border-[var(--border)] last:border-0" data-testid={`account-row-${u.email}`}>
      <div className="flex-1 min-w-[200px]"><div className="text-sm">{u.name || u.email}{u.email === me && <span className="text-xs text-[var(--text-faint)]"> · you</span>}</div><div className="text-xs text-[var(--text-dim)]">{u.email}</div></div>
      <select className={inp} value={u.role} onChange={(e) => setRole(e.target.value)} disabled={u.email === me} data-testid={`account-role-${u.email}`}>{roles.map((r) => <option key={r} value={r}>{ROLE_LABEL[r] || r}</option>)}</select>
      {pw === null ? (
        <button className="btn text-xs" onClick={() => setPw("")} data-testid={`account-reset-${u.email}`}><KeyRound size={12} />Reset password</button>
      ) : (
        <span className="flex gap-1">
          <input autoFocus className={`${inp} w-44`} placeholder="New password (8+)" value={pw} onChange={(e) => setPw(e.target.value)} data-testid={`account-newpw-${u.email}`} />
          <button className="btn btn-primary text-xs" disabled={pw.length < 8} onClick={reset} data-testid={`account-newpw-save-${u.email}`}>Set</button>
          <button className="btn-ghost btn text-xs" onClick={() => setPw(null)}>Cancel</button>
        </span>
      )}
      {u.email !== me && <button className="btn-ghost btn p-1.5 hover:text-[var(--breach)]" title="Remove account" onClick={remove} data-testid={`account-delete-${u.email}`}><Trash2 size={13} /></button>}
    </div>
  );
}

export default function AccountsAdmin() {
  const { user } = useAuth();
  const [d, setD] = useState(null);
  const load = () => api.get("/admin/users").then((r) => setD(r.data));
  useEffect(() => { load(); }, []);
  if (!d) return null;
  return (
    <section className="panel p-4" data-testid="accounts-admin">
      <div className="text-sm font-medium">Team accounts</div>
      <div className="text-xs text-[var(--text-dim)] mb-2">Add people, change their role, or set a new password and share it with them.</div>
      {d.rows.map((u) => <AccountRow key={u.email} u={u} roles={d.roles} me={user?.email} onDone={load} />)}
      <AddAccount roles={d.roles} onDone={load} />
    </section>
  );
}
